import { type LineChartSeries } from "@mantine/charts";
import { Group } from "@mantine/core";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { Text as RechartsText } from "recharts";
import { NoChartDataWrapper } from "../../../../mantine/charts/components/NoChartDataWrapper/NoChartDataWrapper";
import { UNITS } from "../../../../utils/constants";
import { DATE_TIME_DISPLAY_WITHOUT_SECONDS_FORMAT } from "../../../../utils/dates/dates.constants";
import type { Unit } from "../../../../utils/enums";
import { MantineBarChart } from "../../../Charts/BarChart/MantineBarChart";
import { MantineLineChart } from "../../../Charts/LineChart/MantineLineChart";
import { getColoredSeries } from "../../../Charts/utils/getColoredSeries";
import type { CounterData } from "../../../DataSheet/DataSheet";
import { TotalsSideBar } from "../../../RevenueMonitoring/TotalsSideBar/TotalsSideBar";
import { getSeriesColorByMeasurand } from "./getSeriesColorByMeasurand";
import { getSeriesOrderByMeasurand } from "./getSeriesOrderByMeasurand";

interface AcquisitionPlotProps {
  acquisitionSerieses: CounterData;
  selectedUnitValue: Unit;
  sideBarMode?: "sum" | "diff";
  formatTotalsSeriesLabel?: (label: string | undefined) => string | undefined;
}

function AcquisitionPlot({
  acquisitionSerieses,
  selectedUnitValue,
  sideBarMode,
  formatTotalsSeriesLabel
}: AcquisitionPlotProps) {
  const barDataIds = Object.keys(acquisitionSerieses.header);
  const barSeries = [
    {
      name: "value"
    }
  ];
  const barData = barDataIds.map((id) => ({
    category: acquisitionSerieses.labels?.[id]?.replaceAll("<br>", " ") || id,
    value: acquisitionSerieses.values[id][0] ?? "-"
  }));

  const selectedUnit = UNITS[selectedUnitValue];
  const yAxisDescription = selectedUnit.physicalSize;
  const yAxisTitle = `${yAxisDescription} [${selectedUnitValue}]`;
  const valueCount = acquisitionSerieses.index
    ? acquisitionSerieses.index.length
    : 0;

  const dataIds = Object.keys(acquisitionSerieses.header);
  const series: Array<LineChartSeries & { color: string | undefined }> = dataIds
    .sort(getSeriesOrderByMeasurand)
    .map((id) => ({
      name: id,
      label:
        acquisitionSerieses.labels?.[id].replaceAll("<br>", " ") || "Unbekannt",
      color: getSeriesColorByMeasurand(id)
    }));
  const data = useMemo(
    () =>
      acquisitionSerieses.index.map((index, indexIndex) => ({
        date: DateTime.fromISO(index[0]).toMillis(),
        ...dataIds.reduce(
          (result, id) => ({
            ...result,
            [id]: acquisitionSerieses.values[id][indexIndex]
          }),
          {}
        )
      })),
    [acquisitionSerieses, dataIds]
  );

  const sideBarValues = useMemo(() => {
    if (!sideBarMode) return undefined;

    const entries: Array<[string, number | null]> = dataIds.map((id) => {
      const numericValues = acquisitionSerieses.values[id].filter(
        (value): value is number => typeof value === "number"
      );
      if (numericValues.length === 0) return [id, null];

      if (sideBarMode === "sum") {
        return [id, numericValues.reduce((sum, value) => sum + value, 0)];
      }
      if (sideBarMode === "diff") {
        return [id, (numericValues.at(-1) ?? 0) - (numericValues.at(0) ?? 0)];
      }
      return [id, null];
    });

    return Object.fromEntries(entries);
  }, [acquisitionSerieses, dataIds, sideBarMode]);

  if (valueCount === 1) {
    return (
      <MantineBarChart
        containerProps={{ pb: 100 }}
        data={barData}
        dataKey="category"
        h={500}
        series={barSeries}
        tickLine="y"
        xAxisProps={{
          height: 90,
          tick: ({ x, y, payload }) => (
            <RechartsText
              angle={90}
              maxLines={1}
              textAnchor="start"
              verticalAnchor="start"
              width={200}
              x={x}
              y={y}
            >
              {payload.value}
            </RechartsText>
          )
        }}
        yAxisLabel={yAxisTitle}
        yAxisProps={{ width: 75 }}
      />
    );
  } else {
    return (
      <Group align="center" w="100%" wrap="nowrap">
        <NoChartDataWrapper noData={data.length === 0}>
          <MantineLineChart
            data={data}
            dataKey="date"
            h={500}
            legendPosition={sideBarMode ? "top" : "right"}
            series={series}
            withLegend
            xAxisProps={{
              type: "number",
              tickFormatter: (timeStamp) =>
                DateTime.fromMillis(timeStamp).toFormat(
                  DATE_TIME_DISPLAY_WITHOUT_SECONDS_FORMAT
                )
            }}
            yAxisLabel={yAxisTitle}
          />
        </NoChartDataWrapper>

        {sideBarValues && (
          <TotalsSideBar
            mah={500}
            totalsSeries={getColoredSeries(series).map((series) => ({
              label: formatTotalsSeriesLabel?.(series.label) ?? series.label,
              value: sideBarValues[series.name],
              color: series.color
            }))}
            unit={selectedUnitValue}
          />
        )}
      </Group>
    );
  }
}

export { AcquisitionPlot, AcquisitionPlotProps };
